/* global gdAlertify */
import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate } from "../utils/utils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Helper to decode JWT locally
const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const ModalNuevaNovedad = ({ isOpen, onClose, onSuccess, novedadEditar }) => {
    const [observacion, setObservacion] = useState("");
    const [fecha, setFecha] = useState(new Date().toISOString().split("T")[0]);

    useEffect(() => {
        if (isOpen) {
            if (novedadEditar) {
                setObservacion(novedadEditar.observacion || "");
                setFecha(novedadEditar.fecha ? novedadEditar.fecha.split("T")[0] : new Date().toISOString().split("T")[0]);
            } else {
                setObservacion("");
                setFecha(new Date().toISOString().split("T")[0]);
            }
        }
    }, [isOpen, novedadEditar]);

    const handleGuardar = async () => {
        // Validation: Check if editor is empty or just contains HTML tags
        const strippedContent = observacion.replace(/<[^>]*>/g, '').trim();

        if (!strippedContent) {
            gdAlertify.notify("Debes ingresar una observación.", "error");
            return;
        }

        try {
            gdAlertify.showLoader();
            const payload = {
                fecha,
                observacion,
            };

            if (novedadEditar) {
                payload.id = novedadEditar.id;
                // Try to use /editar if it exists, otherwise fall back to logic that might use saving or we assume same endpoint
                // Just using /guardar with ID as previously decided.
                await axiosInstance.post("/novedades/guardar", {
                    ...payload,
                    id: novedadEditar ? novedadEditar.id : null
                });
            } else {
                await axiosInstance.post("/novedades/guardar", payload);
            }

            gdAlertify.hideLoader();
            gdAlertify.notify(novedadEditar ? "Novedad actualizada." : "Novedad guardada.", "success");
            onSuccess();
            onClose();
        } catch (error) {
            gdAlertify.hideLoader();
            console.error("Error al guardar novedad: ", error);
            gdAlertify.notify("Error al guardar la novedad.", "error");
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            // [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
            [{ 'align': [] }],
            // ['clean']                                         // remove formatting button (Tx)
            // ['link', 'image', 'video']                        // link and image, video (Adjuntar)
        ]
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-xl"> {/* Changed to modal-xl for larger size */}
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{novedadEditar ? "Editar Novedad" : "Nueva Novedad"}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Fecha</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fecha}
                                    onChange={(e) => setFecha(e.target.value)}
                                />
                            </div>

                            <div className="mb-3">
                                {/* Removed label "Observación" based on "quites eladjunto el tx" interpretation */}
                                <ReactQuill
                                    theme="snow"
                                    value={observacion}
                                    onChange={setObservacion}
                                    modules={modules}
                                    style={{ height: '300px', marginBottom: '50px' }} // Increased height
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={handleGuardar}>
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

const Novedades = () => {
    const [data, setData] = useState([]);
    const [fechaInicio, setFechaInicio] = useState(() => {
        const now = new Date();
        const inicio = new Date(now.getFullYear(), now.getMonth() - 1, 26);
        return inicio.toISOString().split("T")[0];
    });
    const [fechaFin, setFechaFin] = useState(() => {
        const now = new Date();
        const fin = new Date(now.getFullYear(), now.getMonth(), 26);
        return fin.toISOString().split("T")[0];
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [novedadEditar, setNovedadEditar] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            const decoded = parseJwt(token);
            if (decoded) {
                // Check multiple possible ID fields
                setCurrentUser(decoded.id || decoded.data?.id || decoded.user_id || decoded.sub);
            }
        }
        fetchNovedades();
    }, []);

    const fetchNovedades = async () => {
        try {
            const response = await axiosInstance.get("/novedades/lista", {
                params: { fechaInicio, fechaFin },
            });
            if (response.data && response.data.data) {
                setData(response.data.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error al buscar novedades: ", error);
        }
    };

    const handleEliminar = (id) => {
        gdAlertify.confirm({
            title: "Eliminar Novedad",
            message: "¿Estás seguro de que deseas eliminar esta novedad?",
            onConfirm: async () => {
                try {
                    gdAlertify.showLoader();
                    await axiosInstance.post("/novedades/eliminar", { id });
                    gdAlertify.hideLoader();
                    gdAlertify.notify("Novedad eliminada.", "success");
                    fetchNovedades();
                } catch (error) {
                    gdAlertify.hideLoader();
                    console.error("Error al eliminar novedad: ", error);
                    gdAlertify.notify("Error al eliminar.", "error");
                }
            },
            onCancel: () => { },
        });
    };

    const openModalEditar = (novedad) => {
        setNovedadEditar(novedad);
        setModalOpen(true);
    };

    const openModalNuevo = () => {
        setNovedadEditar(null);
        setModalOpen(true);
    };

    const columns = [
        { name: "id", title: "#", width: 50 },
        { name: "fecha", title: "Fecha", render: (row) => formatDate(row.fecha), width: 100 },
        // Removed Paciente Column
        {
            name: "observacion",
            title: "Observación",
            render: (row) => (
                <div dangerouslySetInnerHTML={{ __html: row.observacion }} />
            )
        },
        {
            name: "acciones",
            title: "Acciones",
            render: (row) => {
                return (
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-warning btn-sm text-white"
                            onClick={() => openModalEditar(row)}
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                        <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleEliminar(row.id)}
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                );
            },
            width: 120,
        },
    ];

    return (
        <div className="container mt-5">
            <div className="card p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>Novedades Coordinación</h3>
                    <button className="btn btn-primary" onClick={openModalNuevo}>
                        Nueva Novedad
                    </button>
                </div>

                <div className="row mb-3">
                    <div className="col-md-3">
                        <label className="form-label">Fecha Inicio</label>
                        <input
                            type="date"
                            className="form-control"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">Fecha Fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                        <button className="btn btn-secondary w-100" onClick={fetchNovedades}>
                            Buscar
                        </button>
                    </div>
                </div>

                <GdTable
                    columns={columns}
                    data={data}
                    defaultItemsPerPage={25}
                    itemsPerPageOptions={[10, 25, 50, 100]}
                    initialSort={[1, "desc"]}
                />
            </div>

            <ModalNuevaNovedad
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={fetchNovedades}
                novedadEditar={novedadEditar}
            />
        </div>
    );
};

export default Novedades;
